/**
 * Cuentas por comensal: helpers compartidos entre la captura (ComandearModal),
 * la vista de mesa, el KDS y la división de cuentas.
 *
 * Un renglón lleva `seatNumber` (1..N) cuando lo pidió un comensal concreto y
 * null cuando es "para compartir". Al dividir, lo compartido se reparte en
 * partes iguales entre los comensales, y las partes se guardan como fracciones
 * de unidad (1/3 = 0.3333) con cuatro decimales.
 */

export const MAX_SEATS = 50;

/** Etiqueta corta del comensal: C1, C2... */
export const seatLabel = (seatNumber: number) => `C${seatNumber}`;

/** Decimales con los que viaja una asignación al backend (numeric(18,4)). */
const SHARE_DECIMALS = 4;

/**
 * Reparte `quantity` unidades entre `parts` cuentas en partes iguales. Las
 * primeras reciben el valor truncado a 4 decimales y la última absorbe el
 * residuo, así la suma da exactamente `quantity`.
 */
export function splitEvenly(quantity: number, parts: number): number[] {
  if (parts <= 0) return [];
  const factor = 10 ** SHARE_DECIMALS;
  const base = Math.floor((quantity / parts) * factor) / factor;
  const shares = Array.from({ length: parts }, () => base);
  const assigned = base * (parts - 1);
  shares[parts - 1] = Math.round((quantity - assigned) * factor) / factor;
  return shares;
}

export const isWholeShare = (share: number) => Math.abs(share - Math.round(share)) < 0.001;

/**
 * "2x" para unidades enteras; "1/3", "2/3", "1 1/2" para fracciones (partes
 * iguales). Sirve para la lista de cuentas, el ticket sintético del cobro y
 * los chips del asignador.
 */
export function formatShare(share: number): string {
  if (isWholeShare(share)) return `${Math.round(share)}x`;
  const whole = Math.floor(share);
  const frac = share - whole;
  for (let d = 2; d <= 10; d++) {
    const n = Math.round(frac * d);
    if (n > 0 && Math.abs(n / d - frac) < 0.002) {
      return whole > 0 ? `${whole} ${n}/${d}` : `${n}/${d}`;
    }
  }
  return share.toFixed(2);
}

/** Mayor comensal usado en una lista de renglones (0 si ninguno). */
export function maxSeatIn(items: { seatNumber?: number | null }[]): number {
  return items.reduce((max, i) => (i.seatNumber && i.seatNumber > max ? i.seatNumber : max), 0);
}

interface SeatAllocatableItem {
  id: string;
  quantity: number;
  seatNumber?: number | null;
}

/**
 * Reparte los renglones entre grupos de comensales, uno por cuenta.
 *
 * Lo que pidió un comensal va entero a la cuenta de su grupo. Lo compartido
 * (sin comensal) se reparte **por persona, no por cuenta**: si el papá paga por
 * él y por el niño, le tocan dos partes de la botella y no la mitad. Repartir
 * por cuenta le cobraría de más al que viene solo.
 *
 * Devuelve itemId → índice de grupo → unidades (fracciones a 4 decimales).
 */
export function allocateBySeatGroups(
  items: SeatAllocatableItem[],
  groups: number[][]
): Record<string, Record<number, number>> {
  const totalPeople = groups.reduce((sum, g) => sum + g.length, 0);
  const groupOfSeat = new Map<number, number>();
  groups.forEach((g, idx) => g.forEach(seat => groupOfSeat.set(seat, idx)));

  const alloc: Record<string, Record<number, number>> = {};
  for (const item of items) {
    alloc[item.id] = {};
    const owner = item.seatNumber ? groupOfSeat.get(item.seatNumber) : undefined;
    if (owner !== undefined) {
      alloc[item.id][owner] = item.quantity;
      continue;
    }
    if (totalPeople === 0) continue;
    const perPerson = splitEvenly(item.quantity, totalPeople);
    let taken = 0;
    groups.forEach((g, idx) => {
      const share = g.reduce((sum) => sum + perPerson[taken++], 0);
      const rounded = Math.round(share * 10 ** SHARE_DECIMALS) / 10 ** SHARE_DECIMALS;
      if (rounded > 0) alloc[item.id][idx] = rounded;
    });
  }
  return alloc;
}
